import * as yup from 'yup'

import type {
  IProductHistory,
  ISchemaForm,
  TaskReasonsResponse,
  ISchemaFormPendingTicketPayload,
  ChainingModal,
  ISchemaFormCloseTicketPayload,
  TicketDetail,
} from 'features/Ticket'
import type {
  ITicketProductInfo,
  Product,
} from 'features/Ticket/TicketProductInfo'
import type { IResponseBranch } from 'features/Branch'
import { PRODUCT_LIMIT } from 'helpers/constants'

type Branch = Pick<
  IResponseBranch,
  'addresses' | 'contacts' | 'companyName' | 'contactName' | 'name'
>

type Context = yup.TestContext & { type?: string }

export const initialForm: ISchemaForm = {
  requestDate: new Date(),
  statusPriority: '',
  type: '',
  taskIssue: '',

  branchDetail: {
    companyId: '',
    companyName: '',
    branchId: '',
    branchName: '',
    branchAreaTh: '',
    branchAreaEn: '',
    branchContactName: '',
    branchContactPhoneNumber: '',
    branchAddress: {
      address: '',
      countryEn: 'Thailand',
      countryTh: 'ไทย',
      subDistrictEn: '',
      subDistrictTh: '',
      districtEn: '',
      districtTh: '',
      provinceEn: '',
      provinceTh: '',
      postalCode: '',
    },
  },
  products: undefined,

  contactName: '',
  branchContactCountryCode: '+66',
  contactPhoneNumber: '',
  issueDetail: '',
}

export const initTaskForProductInfo: ITicketProductInfo = {
  id: '',
  code: '',
  serialNumber: '',
  type: {
    nameTh: '',
    nameEn: '',
  },
  taskIssue: {
    nameTh: '',
    nameEn: '',
  },
}

export const initialFormRequestCloseTicket = {
  completedTaskIssue: '',
  completedDetail: '',
  completedPhoto: [],
}

export const initialFormPendingTicket = {
  pendingReason: '',
  pendingDetail: '',
  pendingPhoto: [],
}

export const initialFormAssignTicket = {
  assignDate: new Date(),
  assignStart: new Date(new Date().setHours(8, 0, 0)),
  assignEnd: new Date(new Date().setHours(9, 0, 0)),
  users: [{ id: '', fullName: '' }],
  statusPriority: '',
  issueDetail: '',
}

export const initialFormTransferringTicket = {
  transferringReasons: [{ id: '' }],
  transferringDetail: '',
  statusPriority: '',
  issueDetail: '',
  users: [{ id: '', fullName: '' }],
  assignDate: new Date(),
  assignStart: new Date(new Date().setHours(8, 0, 0)),
  assignEnd: new Date(new Date().setHours(9, 0, 0)),
}

export const initialProductHistories: IProductHistory[] = [
  {
    id: '',
    data: {
      completedAt: undefined,
      requestDate: undefined,
      userFullName: '-',
      code: '-',
      issueType: {
        id: '',
        nameTh: '',
        nameEn: '',
      },
      detail: '',
      taskId: '-',
      checklist: [
        {
          title: '-',
          name: '-',
          type: 'checkbox',
          isChecked: false,
        },
      ],
    },
  },
]

export const initTaskPendingReasons: TaskReasonsResponse[] = [
  {
    id: '',
    isActive: false,
    mbsClientId: '',
    group: null,
    statusProgress: '',
    reasonEn: '',
    reasonTh: '',
  },
]

export const initTicketDetail: Branch = {
  addresses: [],
  contacts: [],
  companyName: '',
  contactName: '',
  name: '',
}

export const initTicketInfo: TicketDetail = {
  id: '',
  code: '',
  products: [],
  requestDate: '',
  statusPriority: '',
  statusProgress: '',
  users: [{ id: '', fullName: '', phoneNumber: '' }],
  issueDetail: '',
}

export const initProducts: Product[] = [
  {
    id: '',
    name: '',
    code: '',
    model: '',
    serialNumber: '',
    warrantyType: undefined,
    filePath: '',
    photos: [],
    detail: '-',
    startWarranty: undefined,
    endWarranty: undefined,
    skuId: '',
  },
]

export const branchSchema = yup.object().shape({
  companyId: yup.string().required('ticket:product.error'),
  companyName: yup.string().required('ticket:product.error'),
  branchId: yup.string().required('ticket:product.error'),
  branchName: yup.string().required('ticket:product.error'),
})

export const productSchema = yup.object().shape({
  productId: yup.string().required('ticket:product.error'),
})

export const userSchema = yup.object().shape({
  key: yup.string().required('ticket:user.error'),
  label: yup.string(),
  value: yup.string(),
})

export const reasonTransferringSchema = yup.object().shape({
  id: yup.string().required('ticket:pendingReason.error'),
})

export const schemaForm = yup.object().shape({
  requestDate: yup.date().required('ticket:requestDate.error'),
  statusPriority: yup.string().required('ticket:priority.error'),
  type: yup.string().required('ticket:ticketType.error'),
  taskIssue: yup.string().required('ticket:issueType.error'),

  branchDetail: branchSchema,
  products: yup.array().max(PRODUCT_LIMIT).of(productSchema).required(),
  branchContactName: yup.string(),
  branchContactCountryCode: yup.string(),
  branchContactPhoneNumber: yup.string(),
  issueDetail: yup.string().max(1000),
  contactPhoneNumber: yup
    .string()
    .max(15)
    .test(
      'contactPhoneNumber',
      async (contactPhoneNumber, context: Context) => {
        const { path, resolve, createError } = context

        const isPhoneOffice = (phone: string) =>
          phone?.startsWith('02') && phone?.replace('-', '').length !== 9
        const isPhoneNormal = (phone: string) =>
          !phone?.startsWith('02') && phone?.replace('-', '').length !== 10

        if (
          contactPhoneNumber &&
          (isPhoneOffice(contactPhoneNumber) ||
            isPhoneNormal(contactPhoneNumber))
        ) {
          return createError({
            path,
            message: 'ticket:phone.invalid',
          })
        }

        return resolve(true)
      },
    ),
})

export const schemaFormRequestCloseTicket = yup.object().shape({
  completedTaskIssue: yup.string().required('ticket:issueType.error'),
  completedDetail: yup.string().max(1000),
})

export const schemaFormPendingTicket = yup.object().shape({
  pendingReason: yup.string().required('ticket:pendingReason.error'),
  pendingDetail: yup.string(),
  pendingPhoto: yup.mixed<File>().nullable(),
})

export const schemaFormAssignTicket = yup.object().shape({
  assignDate: yup.date(),
  assignStart: yup.date(),
  assignEnd: yup.date(),
  users: yup.array().of(userSchema).required(),
  statusPriority: yup.string(),
  issueDetail: yup.string().max(1000),
})

export const schemaFormTransferringTicket = yup.object().shape({
  transferringReasons: yup
    .array()
    .of(reasonTransferringSchema)
    .required('ticket:reasonTransfer.error'),
  transferringDetail: yup.string().max(1000).required(),
  statusPriority: yup.string(),
  users: yup.array().of(userSchema).required(),
  issueDetail: yup.string().max(1000),
  assignDate: yup.date(),
  assignStart: yup.date(),
  assignEnd: yup.date(),
})

export const initialPayloadForCloseTask: ISchemaFormCloseTicketPayload = {
  completedDetail: '',
  completedTaskIssue: '',
  completedPhoto: [],
}

export const initialPayloadForPendingTask: ISchemaFormPendingTicketPayload = {
  userId: '',
  userFullName: '',
  pendingAt: '',
  pendingReason: '',
  pendingDetail: '',
}

export const initialChainingModal: ChainingModal = {
  openConfirm: false,
  openSuccess: false,
  openCancel: false,
}
